import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import BannerCarousel from '../components/BannerCarousel';
import TopPriorityProducts from '../components/Products/TopPriorityProducts';
import Categories from '../components/Categories';
import FeaturedDeals from '../components/FeaturedDeals';
import ProductGrid from '../components/ProductGrid';
import Testimonials from '../components/Testimonials';
import WhyChooseUs from '../components/WhyChooseUs';
import useStore from '../store/useStore';

gsap.registerPlugin(ScrollTrigger);

/* Reusable hook — fades + slides a section up when it enters the viewport */
const useScrollReveal = (ref, options = {}) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    gsap.fromTo(
      el,
      { y: options.y ?? 18, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: options.duration ?? 0.75,
        ease: options.ease ?? 'power3.out',
        delay: options.delay ?? 0,
        scrollTrigger: {
          trigger: el,
          start: 'top 88%',
          toggleActions: 'play none none none',
        },
      }
    );

    return () => ScrollTrigger.getAll().forEach((t) => t.kill());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};

/* Wrapper that applies the reveal animation to its child section */
const Reveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  useScrollReveal(ref, { delay });
  return <div ref={ref} className="overflow-hidden">{children}</div>;
};

const Home = () => {
  const { fetchCategories, fetchProducts, fetchWishlist, isAuthenticated } = useStore();
  const bannerRef = useRef(null);
  useEffect(() => {
    fetchCategories();
    fetchProducts();
    // sync wishlist for logged-in users on every home-page load
    if (isAuthenticated && typeof fetchWishlist === 'function') {
      fetchWishlist();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps



  /* Banner: scale up + fade in on mount */
  useEffect(() => {
    gsap.fromTo(
      bannerRef.current,
      { scale: 0.97, opacity: 0 },
      { scale: 1, opacity: 1, duration: 0.9, ease: 'power3.out' }
    );
  }, []);

  return (
     <>
      <div ref={bannerRef}>
        <BannerCarousel />
      </div>

      <Reveal delay={0.05}>
        <TopPriorityProducts />
      </Reveal>

      <Reveal delay={0.05}>
        <Categories />
      </Reveal>

      <Reveal delay={0.05}>
        <FeaturedDeals />
      </Reveal>

      <Reveal delay={0.05}>
        <ProductGrid />
      </Reveal>

      <Reveal delay={0.05}>
        <WhyChooseUs />
      </Reveal>

      <Reveal delay={0.05}>
        <Testimonials />
      </Reveal>
  </>
  );
};

export default Home;
